import { useEffect, useRef, useState, useCallback } from "react";

// Auto-sweeping before/after image comparison with drag. Reduced-motion => crossfade.
export function BeforeAfterSlider({ beforeUrl, afterUrl }) {
  const wrapRef = useRef(null);
  const [pos, setPos] = useState(15); // % revealing AFTER (right side)
  const [dragging, setDragging] = useState(false);
  const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Auto sweep (rAF eased ping-pong with pauses). Pauses while dragging / 5s after drag.
  const idleUntil = useRef(0);
  useEffect(() => {
    if (reduced) return;
    let raf, start;
    const dur = 5500, pause = 1000, cycle = (dur + pause) * 2;
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    const loop = (ts) => {
      if (!start) start = ts;
      if (Date.now() >= idleUntil.current && !dragging) {
        const e = (ts - start) % cycle;
        let p;
        if (e < dur) p = ease(e / dur) * 85 + 8;
        else if (e < dur + pause) p = 93;
        else if (e < dur + pause + dur) p = 93 - ease((e - dur - pause) / dur) * 85;
        else p = 8;
        setPos(p);
      } else {
        start = ts - 0; // keep phase from restarting jarringly
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [dragging, reduced]);

  // Reduced-motion crossfade
  const [fadeAfter, setFadeAfter] = useState(false);
  useEffect(() => {
    if (!reduced) return;
    const id = setInterval(() => setFadeAfter((v) => !v), 5000);
    return () => clearInterval(id);
  }, [reduced]);

  const move = useCallback((clientX) => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const p = ((clientX - r.left) / r.width) * 100;
    setPos(Math.max(2, Math.min(98, p)));
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => move(e.touches ? e.touches[0].clientX : e.clientX);
    const onUp = () => { setDragging(false); idleUntil.current = Date.now() + 5000; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
  }, [dragging, move]);

  if (reduced) {
    return (
      <div ref={wrapRef} className="absolute inset-0">
        <img src={beforeUrl} alt="Old roof, before" className="absolute inset-0 w-full h-full object-cover" />
        <img src={afterUrl} alt="New Colorbond roof, after" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1500ms] ${fadeAfter ? "opacity-100" : "opacity-0"}`} />
        <Labels />
      </div>
    );
  }

  return (
    <div ref={wrapRef} data-testid="before-after-slider" className="absolute inset-0 select-none">
      <img src={beforeUrl} alt="Old rusted roof, before" draggable={false} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
        <img src={afterUrl} alt="New Colorbond roof, after" draggable={false} className="absolute inset-0 w-full h-full object-cover" />
      </div>
      <Labels />
      {/* divider + handle */}
      <div className="absolute top-0 bottom-0 z-20" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
        <div className="w-[3px] h-full bg-[#62B848] shadow-[0_0_20px_rgba(98,184,72,0.7)]" />
        <button
          data-testid="slider-handle"
          onMouseDown={(e) => { e.preventDefault(); setDragging(true); }}
          onTouchStart={() => setDragging(true)}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 grid place-items-center w-12 h-12 rounded-full bg-[#62B848] text-[#0c1a12] shadow-xl cursor-ew-resize"
          aria-label="Drag to compare">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M9 6l-4 6 4 6M15 6l4 6-4 6" /></svg>
        </button>
      </div>
    </div>
  );
}

function Labels() {
  return (
    <>
      <span className="absolute top-24 left-5 z-20 text-[11px] font-bold tracking-widest text-white/90 bg-black/40 backdrop-blur px-3 py-1 rounded-full">BEFORE</span>
      <span className="absolute top-24 right-5 z-20 text-[11px] font-bold tracking-widest text-[#0c1a12] bg-[#62B848] px-3 py-1 rounded-full">AFTER</span>
    </>
  );
}
